import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { SearchAddon } from '@xterm/addon-search';
import '@xterm/xterm/css/xterm.css';
import { Eye, EyeOff, Search, X } from 'lucide-react';

interface XTerminalProps {
  instanceId: string;
  interactive?: boolean;
  readOnly?: boolean;
}

export default function XTerminal({
  instanceId,
  interactive = false,
  readOnly = false,
}: XTerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const searchAddonRef = useRef<SearchAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!containerRef.current) return;

    // Create terminal
    const terminal = new Terminal({
      cursorBlink: interactive && !readOnly,
      fontSize: 13,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#0f172a',
        foreground: '#e2e8f0',
        cursor: '#e2e8f0',
        selectionBackground: '#334155',
        black: '#0f172a',
        red: '#ef4444',
        green: '#22c55e',
        yellow: '#eab308',
        blue: '#3b82f6',
        magenta: '#a855f7',
        cyan: '#06b6d4',
        white: '#e2e8f0',
        brightBlack: '#475569',
        brightRed: '#f87171',
        brightGreen: '#4ade80',
        brightYellow: '#fde047',
        brightBlue: '#60a5fa',
        brightMagenta: '#c084fc',
        brightCyan: '#22d3ee',
        brightWhite: '#f8fafc',
      },
      allowProposedApi: true,
      scrollback: 10000,
      convertEol: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    const searchAddon = new SearchAddon();

    terminal.loadAddon(fitAddon);
    terminal.loadAddon(webLinksAddon);
    terminal.loadAddon(searchAddon);

    terminal.open(containerRef.current);
    fitAddon.fit();

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;
    searchAddonRef.current = searchAddon;

    // Connect WebSocket
    const token = localStorage.getItem('token');
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/terminal${token ? `?token=${encodeURIComponent(token)}` : ''}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Attach to instance
      ws.send(JSON.stringify({ type: 'attach', instanceId }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'output':
            terminal.write(msg.data);
            break;
          case 'attached':
            setIsReadOnly(msg.readOnly);
            if (msg.readOnly) {
              terminal.write(
                '\r\n\x1b[33m--- Viewing terminal (read-only) ---\x1b[0m\r\n',
              );
            }
            break;
          case 'exit':
            terminal.write(
              `\r\n\x1b[90m--- Process exited (code ${msg.code}) ---\x1b[0m\r\n`,
            );
            break;
          case 'error':
            terminal.write(`\r\n\x1b[31mError: ${msg.message}\x1b[0m\r\n`);
            break;
        }
      } catch {
        // ignore
      }
    };

    ws.onclose = () => {
      setConnected(false);
      terminal.write('\r\n\x1b[90m--- Disconnected ---\x1b[0m\r\n');
    };

    ws.onerror = () => {
      setConnected(false);
    };

    // Forward terminal input to WebSocket
    const inputDisposable = terminal.onData((data) => {
      if (interactive && !isReadOnly && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input', data }));
      }
    });

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      fitAddon.fit();
    });
    resizeObserver.observe(containerRef.current);

    const resizeDisposable = terminal.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols, rows }));
      }
    });

    return () => {
      inputDisposable.dispose();
      resizeDisposable.dispose();
      resizeObserver.disconnect();
      ws.close();
      terminal.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
      searchAddonRef.current = null;
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  // Update cursor blink when interactive/readOnly changes
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.options.cursorBlink = interactive && !isReadOnly;
    }
  }, [interactive, isReadOnly]);

  // Handle search
  useEffect(() => {
    if (!searchAddonRef.current) return;
    if (searchQuery) {
      searchAddonRef.current.findNext(searchQuery);
    }
  }, [searchQuery]);

  const handleSearchNext = () => {
    searchAddonRef.current?.findNext(searchQuery);
  };

  const handleSearchPrev = () => {
    searchAddonRef.current?.findPrevious(searchQuery);
  };

  return (
    <div className="flex flex-col rounded-lg border border-slate-700 bg-slate-900 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="font-medium text-slate-300">Terminal</span>
          <span
            className={`flex items-center gap-1 ${connected ? 'text-green-400' : 'text-yellow-400'}`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${connected ? 'bg-green-400' : 'bg-yellow-400'}`}
            />
            {connected ? 'Connected' : 'Connecting...'}
          </span>
          {isReadOnly && (
            <span className="flex items-center gap-1 text-yellow-400">
              <Eye className="h-3 w-3" />
              Read-only
            </span>
          )}
          {interactive && !isReadOnly && (
            <span className="flex items-center gap-1 text-green-400">
              <EyeOff className="h-3 w-3" />
              Interactive
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className={`rounded p-1 text-xs ${showSearch ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <Search className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Search bar */}
      {showSearch && (
        <div className="flex items-center gap-2 border-b border-slate-700 px-4 py-1.5">
          <Search className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void (e.shiftKey ? handleSearchPrev() : handleSearchNext());
              }
              if (e.key === 'Escape') {
                setShowSearch(false);
                setSearchQuery('');
              }
            }}
            placeholder="Search terminal..."
            className="flex-1 bg-transparent text-xs text-slate-200 placeholder-slate-500 outline-none"
            autoFocus
          />
          <button
            onClick={handleSearchPrev}
            className="rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-700 hover:text-slate-200"
          >
            Prev
          </button>
          <button
            onClick={handleSearchNext}
            className="rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-700 hover:text-slate-200"
          >
            Next
          </button>
          <button
            onClick={() => {
              setShowSearch(false);
              setSearchQuery('');
            }}
            className="text-slate-500 hover:text-slate-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Terminal container */}
      <div
        ref={containerRef}
        className="h-96 w-full"
        style={{ padding: '8px' }}
      />
    </div>
  );
}
